/**
 * SessionExpiredModal - Displayed when session expires
 * 
 * Listens for SESSION_EXPIRED_EVENT from apiClient and shows a clean modal
 * prompting the user to log in again.
 */

import React, { useState, useEffect } from 'react';
import { SESSION_EXPIRED_EVENT } from '../../utils/apiClient';

const SessionExpiredModal = ({ onLogin }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [reason, setReason] = useState('');
  
  useEffect(() => {
    const handleSessionExpired = (event) => {
      console.log('[SessionExpiredModal] Session expired:', event.detail);
      setReason(event.detail?.reason || 'unknown');
      setIsVisible(true);
    };
    
    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, []);
  
  const handleLoginClick = () => {
    setIsVisible(false);
    if (onLogin) {
      onLogin();
    }
  };
  
  if (!isVisible) return null;
  
  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.iconContainer}>
          <span style={styles.icon}>🔒</span>
        </div>
        
        <h2 style={styles.title}>Session Expired</h2>
        
        <p style={styles.message}>
          Your session has expired. Please log in again to continue.
        </p>
        
        {reason === 'forbidden' && (
          <p style={styles.subMessage}>
            You may not have permission to access this resource.
          </p>
        )}
        
        <button 
          onClick={handleLoginClick}
          style={styles.loginButton}
          data-testid="session-expired-login-btn"
        >
          Log In
        </button>
        
        <button 
          onClick={() => setIsVisible(false)}
          style={styles.dismissButton}
        >
          Dismiss
        </button>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '20px'
  },
  modal: {
    background: '#0d0d14',
    borderRadius: '16px',
    padding: '32px 24px',
    maxWidth: '360px',
    width: '100%',
    textAlign: 'center',
    border: '1px solid rgba(255, 255, 255, 0.08)'
  },
  iconContainer: {
    width: '64px',
    height: '64px',
    background: 'rgba(239, 68, 68, 0.1)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px'
  },
  icon: {
    fontSize: '32px'
  },
  title: {
    color: '#fff',
    fontSize: '20px',
    fontWeight: '600',
    margin: '0 0 12px',
    fontFamily: "'Space Grotesk', system-ui, sans-serif"
  },
  message: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '14px',
    margin: '0 0 8px',
    lineHeight: '1.5'
  },
  subMessage: {
    color: '#f59e0b',
    fontSize: '13px',
    margin: '0 0 20px',
    lineHeight: '1.4'
  },
  loginButton: {
    width: '100%',
    padding: '14px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    border: 'none',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginBottom: '12px'
  },
  dismissButton: {
    width: '100%',
    padding: '12px',
    background: 'transparent',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '10px',
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '14px',
    cursor: 'pointer'
  }
};

export default SessionExpiredModal;
